import { lastSentTouch } from './types';
import type { Membership, SequenceStatus, Touch } from './types';

/**
 * What day the Outreach grid records an action as happening on.
 *
 * ── Why this module exists ─────────────────────────────────────────────────
 *
 * Until now the grid stamped `todayIso()` into every date it wrote, with no
 * way to say otherwise:
 *
 *   reply_date: todayIso()
 *   [`touch${touch}_sent_on`]: todayIso()
 *
 * That is fine while you are working the list live and wrong the moment you
 * are catching up. Kyu's team spent a week back-tagging replies that had
 * arrived in June and July; every one of them landed in the database dated
 * the day it was typed. Sara Weaver replied on 23 June and the Hub said
 * 26 August. Six leads carry a reply date of 7 August, which is not six
 * people replying on a Friday — it is one tagging session.
 *
 * The lead drawer's activity log has had a date field the whole time, and the
 * three replies logged through it are all correctly back-dated. The grid was
 * the only path that could not tell the truth, so it is the path that changed.
 *
 * ── Why the logic is here rather than in the component ─────────────────────
 *
 * Drop 8h learned this the hard way: a sabotage that flipped a saved value
 * passed all 341 tracker tests, because the component tests asserted against
 * mocked callbacks and never looked at what was being written. The fix was to
 * extract the payload into a pure function and assert on THAT.
 *
 * Same shape here. These functions build the exact rows the grid saves, so a
 * test can prove the chosen date reaches the database rather than proving a
 * button was clicked.
 */

export interface MarkSentChanges {
  [column: string]: string;
}

/** The columns a "Mark sent" writes, dated. */
export function markSentChanges(touch: Touch, on: string): MarkSentChanges {
  return {
    [`touch${String(touch)}_status`]: 'Sent',
    [`touch${String(touch)}_sent_on`]: on,
  };
}

export interface MarkRepliedChanges {
  seq_status: SequenceStatus;
  reply_touch: Touch;
  reply_date: string;
}

/**
 * The columns a "Replied" writes, dated.
 *
 * `reply_touch` still comes from the membership's own touch history rather
 * than from the date — which touch they replied to is a fact about the cycle,
 * not about when you typed it. Falls back to 1 for a reply on a lead whose
 * sends were never recorded, which is the pre-existing behaviour and is
 * deliberately unchanged.
 */
export function markRepliedChanges(membership: Membership, on: string): MarkRepliedChanges {
  return {
    seq_status: 'replied',
    reply_touch: lastSentTouch(membership) ?? 1,
    reply_date: on,
  };
}

/**
 * Whether the grid is currently recording into the past.
 *
 * The date control sits above the table and holds its value for the whole
 * session, which is what makes a catch-up session quick — set it once, tag
 * twenty rows. It is also how somebody marks a live send with last Tuesday's
 * date because they forgot they had changed it.
 *
 * So the grid says so, loudly, whenever this is true. A control that silently
 * governs every write on the screen is exactly the kind of thing this module
 * keeps finding after the fact.
 */
export function isBackdated(actionDate: string, today: string): boolean {
  return actionDate !== '' && actionDate < today;
}

/**
 * The date to actually write.
 *
 * An empty control means today rather than an empty column. A browser can
 * hand back '' for a date input the user cleared, and writing that into
 * `reply_date` would produce a reply with no date at all — which reads
 * everywhere as "never replied".
 */
export function actionDateOrToday(actionDate: string, today: string): string {
  return actionDate === '' ? today : actionDate;
}
